import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Container, Form, Button, Alert, Spinner, Card, Image, Row, Col, ProgressBar } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { 
  FaUpload, 
  FaCloudUploadAlt, 
  FaImages, 
  FaFileAlt, 
  FaTrash, 
  FaCheckCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaImage,
  FaVideo,
  FaMusic,
  FaFilePdf,
  FaStar,
  FaBolt,
  FaLanguage,
  FaHome,
  FaPlus,
  FaTimes,
  FaPlayCircle,
  FaHeadphones,
  FaFileWord,
  FaFileExcel,
  FaFilePowerpoint,
  FaFile,
  FaArrowLeft,
  FaCog,
  FaBell,
  FaUser,
  FaBars
} from 'react-icons/fa';

const UploadPage = () => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content_type: 'image',
    category: 'media',
    author: 'أمجاد الأحرار',
    tags: '',
    language: 'ar'
  });
  const [featuredMedia, setFeaturedMedia] = useState(null);
  const [additionalMedia, setAdditionalMedia] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const [categories, setCategories] = useState([]);
  const [contentTypes, setContentTypes] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const navigate = useNavigate();

  // Load categories and content types from database
  useEffect(() => {
    const loadData = async () => {
      try {
        // Load categories - only media related
        const { data: categoriesData, error: catError } = await supabase
          .from('categories')
          .select('*')
          .order('sort_order');

        if (catError) {
          console.error('Error loading categories:', catError);
          // Use only media categories
          setCategories([
            { id: 3, name: 'إعلامي', slug: 'media', icon: '📰', color: '#8b5cf6' }
          ]);
        } else {
          // Filter to only include media category
          const filteredCategories = categoriesData.filter(cat => 
            cat.slug === 'media' || 
            cat.name?.toLowerCase().includes('media') || 
            cat.name?.toLowerCase().includes('إعلام') ||
            cat.name_ar?.includes('إعلامي')
          );
          
          const formattedCategories = filteredCategories.map(cat => ({
            ...cat,
            name: cat.name_ar || cat.name,
            icon: '📰',
            color: cat.color || '#8b5cf6'
          }));
          
          // If no media category found, use default
          if (formattedCategories.length === 0) {
            setCategories([
              { id: 3, name: 'إعلامي', slug: 'media', icon: '📰', color: '#8b5cf6' }
            ]);
          } else {
            setCategories(formattedCategories);
          }
        }

        // Load content types - only images and videos
        const { data: contentTypesData, error: typeError } = await supabase
          .from('content_types')
          .select('*')
          .order('sort_order');

        if (typeError) {
          console.error('Error loading content types:', typeError);
          // Use only image and video content types
          setContentTypes([
            { id: 1, name: 'صورة', slug: 'image', icon: '🖼️', color: '#10b981' },
            { id: 2, name: 'فيديو', slug: 'video', icon: '🎬', color: '#3b82f6' }
          ]);
        } else {
          // Filter to only include image and video content types
          const filteredTypes = contentTypesData.filter(type => 
            type.slug === 'image' || type.slug === 'video' || 
            type.name?.toLowerCase().includes('image') || 
            type.name?.toLowerCase().includes('video') ||
            type.name_ar?.includes('صورة') || 
            type.name_ar?.includes('فيديو')
          );
          
          const formattedTypes = filteredTypes.map(type => ({
            ...type,
            name: type.name_ar || type.name,
            icon: type.icon || (type.slug === 'image' ? '🖼️' : '🎬'),
            color: type.color || (type.slug === 'image' ? '#10b981' : '#3b82f6')
          }));
          
          // If no matching types found, use defaults
          if (formattedTypes.length === 0) {
            setContentTypes([
              { id: 1, name: 'صورة', slug: 'image', icon: '🖼️', color: '#10b981' },
              { id: 2, name: 'فيديو', slug: 'video', icon: '🎬', color: '#3b82f6' }
            ]);
          } else {
            setContentTypes(formattedTypes);
          }
        }
      } catch (err) {
        console.error('Error loading data:', err);
        setError('فشل تحميل البيانات');
      }
    };

    loadData();
  }, []);

  // Load Cloudinary widget
  useEffect(() => {
    const scriptId = 'cloudinary-widget-script';
    
    // Remove existing script if any
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.src = 'https://upload-widget.cloudinary.com/global/all.js';
    script.async = true;
    
    script.onload = () => {
      console.log('Cloudinary widget loaded');
      setWidgetLoaded(true);
    };
    
    script.onerror = () => {
      console.error('Failed to load Cloudinary widget');
      setError('فشل تحميل أداة الرفع');
    };
    
    document.body.appendChild(script);

    return () => {
      const script = document.getElementById(scriptId);
      if (script) {
        script.remove();
      }
    };
  }, []);

  const openCloudinaryWidget = (isFeatured = false, isDocument = false) => {
    if (!widgetLoaded) {
      setError('أداة الرفع لا تزال تحمّل. يرجى المحاولة مرة أخرى.');
      return;
    }

    if (!window.cloudinary || !window.cloudinary.createUploadWidget) {
      setError('أداة الرفع غير متاحة. يرجى تحديث الصفحة.');
      return;
    }

    try {
      const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'djcq8y6ml';
      const uploadPreset = isDocument 
        ? (process.env.REACT_APP_CLOUDINARY_DOC_UPLOAD_PRESET || 'irdaa-documents')
        : (process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'irda-media');

      // Restrict to images and videos only for media uploads
      const allowedFormats = isDocument 
        ? ['pdf', 'doc', 'docx', 'txt', 'xlsx', 'pptx']
        : ['jpg', 'jpeg', 'png', 'gif', 'webp', 'mp4', 'mov', 'avi'];

      const widgetOptions = {
        cloudName: cloudName,
        uploadPreset: uploadPreset,
        sources: ['local', 'url'],
        multiple: !isFeatured,
        maxFiles: isFeatured ? 1 : 10,
        resourceType: isDocument ? 'raw' : 'auto',
        maxFileSize: 100 * 1024 * 1024, // 100MB limit
        showAdvancedOptions: false,
        cropping: false,
        styles: {
          palette: {
            window: '#111111',
            windowBorder: '#2a2a2a',
            tabIcon: '#6366f1',
            menuIcons: '#a3a3a3',
            textDark: '#ffffff',
            textLight: '#ffffff',
            link: '#6366f1',
            action: '#6366f1',
            inactiveTabIcon: '#525252',
            error: '#ef4444',
            inProgress: '#6366f1',
            complete: '#10b981',
            sourceBg: '#1a1a1a'
          }
        },
        clientAllowedFormats: allowedFormats
      };

      const uploadWidget = window.cloudinary.createUploadWidget(
        widgetOptions,
        (error, result) => {
          if (error) {
            console.error('Upload error:', error);
            setError('خطأ في الرفع');
            return;
          }

          if (result && result.event === 'success') {
            const mediaData = {
              url: result.info.secure_url,
              public_id: result.info.public_id,
              format: result.info.format,
              size: result.info.bytes,
              resource_type: result.info.resource_type,
              original_filename: result.info.original_filename,
              width: result.info.width,
              height: result.info.height,
              duration: result.info.duration
            };

            if (isDocument) {
              setDocuments(prev => [...prev, mediaData]);
            } else if (isFeatured) {
              setFeaturedMedia(mediaData);
            } else {
              setAdditionalMedia(prev => [...prev, mediaData]);
            }
          }
        }
      );

      uploadWidget.open();
    } catch (err) {
      console.error('Widget error:', err);
      setError('خطأ في فتح أداة الرفع');
    }
  };

  const removeMedia = (index, isFeatured = false, isDocument = false) => {
    if (isDocument) {
      setDocuments(prev => prev.filter((_, i) => i !== index));
    } else if (isFeatured) {
      setFeaturedMedia(null);
    } else {
      setAdditionalMedia(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContentTypeChange = (value) => {
    setFormData(prev => ({
      ...prev,
      content_type: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setUploadProgress(0);
  
    // Validation
    if (!formData.title.trim() || !formData.description.trim() || 
        !formData.content_type || !formData.category || !formData.author.trim()) {
      setError('يرجى ملء جميع الحقول المطلوبة');
      setLoading(false);
      return;
    }
  
    try {
      setUploadProgress(20);
      const token = uuidv4();
      const now = new Date().toISOString();
  
      // Prepare tags array
      const tagsArray = formData.tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);
  
      setUploadProgress(40);
  
      // Prepare content data
      const contentData = {
        title: formData.title,
        title_ar: formData.title,
        description: formData.description,
        description_ar: formData.description,
        content_type: formData.content_type,
        category: formData.category,
        author: formData.author,
        author_ar: formData.author,
        // Optional fields:
        featured_media: featuredMedia ? featuredMedia.url : null,
        featured_media_data: featuredMedia || null,
        additional_media: additionalMedia,
        documents: documents,
        tags: tagsArray,
        tags_ar: tagsArray,
        status: 'published',
        is_featured: false,
        is_breaking: false,
        language: formData.language,
        token: token,
        views: 0,
        likes: 0,
        shares: 0,
        comments_count: 0,
        published_at: now,
        created_at: now,
        updated_at: now
      };
  
      setUploadProgress(60);
  
      // Insert into content table
      const { data, error: supabaseError } = await supabase
        .from('content')
        .insert([contentData])
        .select();
  
      if (supabaseError) {
        console.error('Supabase error:', supabaseError);
        throw new Error(supabaseError.message || 'فشل حفظ المحتوى');
      }
  
      setUploadProgress(80);
  
      // Also insert into content_tokens table
      if (data && data[0]) {
        const { error: tokenError } = await supabase
          .from('content_tokens')
          .insert([{
            token: token,
            content_id: data[0].id,
            title: formData.title,
            title_ar: formData.title,
            status: 'active',
            type: formData.content_type,
            created_at: now,
            updated_at: now
          }]);
  
        if (tokenError) {
          console.warn('Could not save token:', tokenError);
        }
      }
  
      setUploadProgress(100);
      setSuccess(true);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          title: '',
          description: '',
          content_type: 'image',
          category: 'media',
          author: 'أمجاد الأحرار',
          tags: '',
          language: 'ar'
        });
        setFeaturedMedia(null);
        setAdditionalMedia([]);
        setDocuments([]);
        setSuccess(false);
        setUploadProgress(0);
      }, 3000);
  
    } catch (err) {
      console.error('Submit error:', err);
      setError(`خطأ في حفظ المحتوى: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      content_type: 'image',
      category: 'media',
      author: 'أمجاد الأحرار',
      tags: '',
      language: 'ar'
    });
    setFeaturedMedia(null);
    setAdditionalMedia([]);
    setDocuments([]);
    setSuccess(false);
    setError(null);
    setUploadProgress(0);
  };

  const getFileIcon = (format, resourceType) => {
    if (resourceType === 'image') return <FaImage className="text-primary" />;
    if (resourceType === 'video') return <FaVideo className="text-danger" />;
    
    switch(format?.toLowerCase()) {
      case 'pdf': return <FaFilePdf className="text-danger" />;
      case 'doc': case 'docx': return <FaFileWord className="text-primary" />;
      case 'xls': case 'xlsx': return <FaFileExcel className="text-success" />;
      case 'ppt': case 'pptx': return <FaFilePowerpoint className="text-warning" />;
      default: return <FaFile className="text-secondary" />;
    }
  };

  // Format bytes to human readable
  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 بايت';
    
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['بايت', 'كيلوبايت', 'ميجابايت', 'جيجابايت'];
    
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  return (
    <div className="app">
      <style>{`
        /* Reset and Base Styles */
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          background: #0a0a0a;
        }

        .app {
          min-height: 100vh;
          background: #0a0a0a;
          font-family: 'ITF Qomra Arabic', 'Segoe UI', sans-serif;
          direction: rtl;
          color: #ffffff;
          padding: 2rem;
        }

        /* Upload Container */
        .upload-container {
          max-width: 1200px;
          margin: 0 auto;
        }

        .upload-header {
          margin-bottom: 2rem;
        }

        .upload-header h1 {
          font-size: 2rem;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 0.5rem;
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .upload-header p {
          color: #a3a3a3;
          font-size: 1rem;
        }

        /* Upload Card */
        .upload-card {
          background: #111111;
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 24px;
          overflow: hidden;
        }

        .card-body {
          padding: 2rem;
        }

        /* Form Sections */
        .section-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: #ffffff;
          font-size: 1.2rem;
          font-weight: 600;
          margin-bottom: 1.5rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.05);
        }

        .section-header svg {
          color: #818cf8;
        }

        /* Form Controls */
        .form-label {
          color: #a3a3a3;
          font-weight: 500;
          margin-bottom: 0.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .form-control-custom {
          background: #1a1a1a;
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 0.75rem 1rem;
          color: #ffffff;
          font-size: 0.95rem;
          width: 100%;
          transition: all 0.2s ease;
          font-family: 'ITF Qomra Arabic', sans-serif;
        }

        .form-control-custom:focus {
          outline: none;
          border-color: #6366f1;
          background: #1f1f1f;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
          color: #ffffff;
        }

        .form-control-custom::placeholder {
          color: #525252;
        }

        textarea.form-control-custom {
          min-height: 120px;
          resize: vertical;
        }

        /* Radio Button Container */
        .radio-container {
          display: flex;
          gap: 2rem;
          padding: 0.5rem 0;
        }

        .radio-option {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1.5rem;
          background: #1a1a1a;
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 40px;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .radio-option:hover {
          border-color: #6366f1;
          background: #1f1f1f;
        }

        .radio-option input[type="radio"] {
          width: 20px;
          height: 20px;
          accent-color: #6366f1;
          cursor: pointer;
        }

        .radio-option label {
          color: #ffffff;
          font-weight: 500;
          cursor: pointer;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .radio-option svg {
          font-size: 1.2rem;
        }

        .radio-option.image svg { color: #10b981; }
        .radio-option.video svg { color: #3b82f6; }

        /* Category Display */
        .category-display {
          background: #1a1a1a;
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 12px;
          padding: 0.75rem 1rem;
          color: #ffffff;
          font-size: 0.95rem;
          width: 100%;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .category-display svg {
          color: #8b5cf6;
        }

        /* Upload Button */
        .upload-button {
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 40px;
          color: #ffffff;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          transition: all 0.2s ease;
          font-size: 0.95rem;
          font-family: 'ITF Qomra Arabic', sans-serif;
        }

        .upload-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 15px rgba(99, 102, 241, 0.3);
        }

        .upload-button:disabled {
          background: #2a2a2a;
          color: #737373;
          cursor: not-allowed;
          transform: none;
        }

        /* Media Preview */
        .media-preview-container {
          background: #1a1a1a;
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 16px;
          padding: 1.5rem;
          margin-top: 1rem;
        }

        .media-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 1rem;
        }

        .media-item {
          background: #111111;
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 12px;
          padding: 1rem;
          position: relative;
          transition: all 0.2s ease;
        }

        .media-item:hover {
          border-color: #6366f1;
          transform: translateY(-2px);
        }

        .media-preview {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .media-icon {
          width: 50px;
          height: 50px;
          background: #1a1a1a;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
        }

        .media-icon.image { color: #3b82f6; }
        .media-icon.video { color: #ef4444; }
        .media-icon.pdf { color: #ef4444; }
        .media-icon.word { color: #3b82f6; }
        .media-icon.excel { color: #10b981; }
        .media-icon.powerpoint { color: #f59e0b; }

        .media-info {
          flex: 1;
          min-width: 0;
        }

        .media-name {
          font-size: 0.9rem;
          font-weight: 600;
          color: #ffffff;
          margin-bottom: 0.25rem;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .media-meta {
          font-size: 0.75rem;
          color: #737373;
        }

        .remove-btn {
          position: absolute;
          top: 0.5rem;
          left: 0.5rem;
          width: 24px;
          height: 24px;
          background: #dc2626;
          border: none;
          border-radius: 6px;
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 0.8rem;
          opacity: 0;
        }

        .media-item:hover .remove-btn {
          opacity: 1;
        }

        .remove-btn:hover {
          background: #b91c1c;
          transform: scale(1.1);
        }

        /* Featured Media */
        .featured-media {
          background: #1a1a1a;
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 16px;
          padding: 1.5rem;
          margin-top: 1rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1.5rem;
        }

        .featured-preview {
          display: flex;
          align-items: center;
          gap: 1.5rem;
          flex: 1;
        }

        .featured-thumb {
          width: 100px;
          height: 100px;
          border-radius: 12px;
          overflow: hidden;
          background: #111111;
        }

        .featured-thumb img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .featured-placeholder {
          width: 100px;
          height: 100px;
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          color: #ffffff;
        }

        .featured-info {
          flex: 1;
        }

        .featured-title {
          font-size: 1rem;
          font-weight: 600;
          color: #ffffff;
          margin-bottom: 0.5rem;
        }

        .featured-meta {
          font-size: 0.85rem;
          color: #737373;
        }

        /* Empty State */
        .empty-state {
          text-align: center;
          padding: 2rem;
          background: #1a1a1a;
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 16px;
          color: #737373;
        }

        .empty-state svg {
          font-size: 2rem;
          margin-bottom: 1rem;
          color: #404040;
        }

        .empty-state p {
          margin: 0;
        }

        /* Submit Button */
        .submit-button {
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          border: none;
          padding: 1rem 2rem;
          border-radius: 40px;
          color: #ffffff;
          font-weight: 600;
          font-size: 1.1rem;
          width: 100%;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          margin-top: 2rem;
          font-family: 'ITF Qomra Arabic', sans-serif;
        }

        .submit-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(99, 102, 241, 0.3);
        }

        .submit-button:disabled {
          background: #2a2a2a;
          color: #737373;
          cursor: not-allowed;
        }

        /* Alerts */
        .alert {
          padding: 1rem 1.5rem;
          border-radius: 12px;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          border: 1px solid transparent;
        }

        .alert-error {
          background: rgba(220, 38, 38, 0.1);
          border-color: #dc2626;
          color: #ef4444;
        }

        .alert-success {
          background: rgba(16, 185, 129, 0.1);
          border-color: #10b981;
          color: #10b981;
        }

        /* Progress Bar */
        .progress-container {
          margin-bottom: 2rem;
        }

        .progress-header {
          display: flex;
          justify-content: space-between;
          color: #a3a3a3;
          font-size: 0.9rem;
          margin-bottom: 0.5rem;
        }

        .progress {
          height: 8px;
          background: #1a1a1a;
          border-radius: 4px;
          overflow: hidden;
        }

        .progress-bar {
          background: linear-gradient(90deg, #6366f1, #8b5cf6);
          transition: width 0.3s ease;
        }

        /* Success Container */
        .success-container {
          text-align: center;
          padding: 3rem;
        }

        .success-icon {
          font-size: 4rem;
          color: #10b981;
          margin-bottom: 1.5rem;
        }

        .success-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 1rem;
        }

        .success-text {
          color: #a3a3a3;
          margin-bottom: 2rem;
        }

        .action-buttons {
          display: flex;
          gap: 1rem;
          justify-content: center;
        }

        .action-button {
          padding: 0.75rem 2rem;
          border-radius: 40px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          border: none;
          font-size: 1rem;
        }

        .action-button.primary {
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          color: #ffffff;
        }

        .action-button.primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 15px rgba(99, 102, 241, 0.3);
        }

        .action-button.secondary {
          background: #1a1a1a;
          border: 1px solid rgba(255, 255, 255, 0.1);
          color: #ffffff;
        }

        .action-button.secondary:hover {
          background: #2a2a2a;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .app {
            padding: 1rem;
          }
          
          .upload-container {
            padding: 0;
          }
          
          .card-body {
            padding: 1.5rem;
          }
          
          .media-grid {
            grid-template-columns: 1fr;
          }
          
          .featured-media {
            flex-direction: column;
            align-items: flex-start;
          }
          
          .featured-preview {
            width: 100%;
          }
          
          .action-buttons {
            flex-direction: column;
          }

          .radio-container {
            flex-direction: column;
            gap: 1rem;
          }

          .radio-option {
            width: 100%;
          }
        }
      `}</style>

      {/* Upload Container */}
      <div className="upload-container">
        <div className="upload-header">
          <h1>
            <FaCloudUploadAlt />
            رفع محتوى جديد
          </h1>
          <p>املأ المعلومات الأساسية وارفع الصور أو الفيديوهات لنشر المحتوى على المنصة</p>
        </div>

        <Card className="upload-card">
          <div className="card-body">
            {success ? (
              <div className="success-container">
                <FaCheckCircle className="success-icon" />
                <h2 className="success-title">تم النشر بنجاح!</h2>
                <p className="success-text">تم نشر محتواك على المنصة وسيكون متاحاً للجميع.</p>
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <div className="progress-container">
                    <div className="progress-header">
                      <span>جاري النشر...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="progress">
                      <div className="progress-bar" style={{ width: `${uploadProgress}%` }} />
                    </div>
                  </div>
                )}
                <div className="action-buttons">
                  <button 
                    className="action-button primary"
                    onClick={() => navigate('/')}
                  >
                    <FaHome />
                    العودة للرئيسية
                  </button>
                  <button 
                    className="action-button secondary"
                    onClick={resetForm}
                  >
                    <FaPlus />
                    رفع محتوى جديد
                  </button>
                </div>
              </div>
            ) : (
              <Form onSubmit={handleSubmit}>
                {error && (
                  <div className="alert alert-error">
                    <FaExclamationTriangle />
                    {error}
                  </div>
                )}

                {/* Upload Progress */}
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <div className="progress-container">
                    <div className="progress-header">
                      <span>جاري النشر...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="progress">
                      <div className="progress-bar" style={{ width: `${uploadProgress}%` }} />
                    </div>
                  </div>
                )}

                {/* Basic Information Section */}
                <div className="mb-5">
                  <h4 className="section-header">
                    <FaInfoCircle />
                    المعلومات الأساسية
                  </h4>

                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-4">
                        <Form.Label className="form-label">
                          <FaFileAlt />
                          العنوان *
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="title"
                          value={formData.title}
                          onChange={handleInputChange}
                          required
                          placeholder="أدخل عنوان المحتوى"
                          className="form-control-custom"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-4">
                        <Form.Label className="form-label">
                          <FaFileAlt />
                          الوصف *
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          required
                          placeholder="صف محتواك"
                          className="form-control-custom"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-4">
                        <Form.Label className="form-label">
                          <FaFileAlt />
                          نوع المحتوى *
                        </Form.Label>
                        <div className="radio-container">
                          {contentTypes.map((type) => (
                            <div 
                              key={type.slug || type.id} 
                              className={`radio-option ${type.slug}`}
                              onClick={() => handleContentTypeChange(type.slug || type.value)}
                            >
                              <input
                                type="radio"
                                name="content_type"
                                value={type.slug || type.value}
                                checked={formData.content_type === (type.slug || type.value)}
                                onChange={(e) => handleContentTypeChange(e.target.value)}
                                id={`content-type-${type.slug}`}
                              />
                              <label htmlFor={`content-type-${type.slug}`}>
                                {type.slug === 'image' ? <FaImage /> : <FaVideo />}
                                {type.name}
                              </label>
                            </div>
                          ))}
                        </div>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-4">
                        <Form.Label className="form-label">
                          <FaFileAlt />
                          التصنيف *
                        </Form.Label>
                        <div className="category-display">
                          <FaFileAlt />
                          {categories.length > 0 ? categories[0].name : 'إعلامي'}
                        </div>
                        <input type="hidden" name="category" value="media" />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-4">
                        <Form.Label className="form-label">
                          <FaUser />
                          المؤلف *
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="author"
                          value={formData.author}
                          onChange={handleInputChange}
                          required
                          placeholder="أدخل اسم المؤلف"
                          className="form-control-custom"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-4">
                        <Form.Label className="form-label">
                          <FaLanguage />
                          لغة المحتوى
                        </Form.Label>
                        <Form.Select
                          name="language"
                          value={formData.language}
                          onChange={handleInputChange}
                          className="form-control-custom"
                        >
                          <option value="ar">العربية</option>
                          <option value="en">الإنجليزية</option>
                          <option value="both">كلاهما</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-4">
                        <Form.Label className="form-label">
                          <FaFileAlt />
                          الكلمات الدلالية
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="tags"
                          value={formData.tags}
                          onChange={handleInputChange}
                          placeholder="كلمات دلالية مفصولة بفواصل"
                          className="form-control-custom"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* Featured Media Section */}
                <div className="mb-5">
                  <h4 className="section-header">
                    <FaStar />
                    الوسائط الرئيسية
                  </h4>
                  
                  <button 
                    type="button"
                    onClick={() => openCloudinaryWidget(true, false)}
                    className="upload-button"
                    disabled={uploading}
                  >
                    <FaCloudUploadAlt />
                    {uploading ? 'جاري الرفع...' : 'رفع وسائط رئيسية'}
                  </button>
                  
                  <small className="d-block text-muted mt-2" style={{ color: '#737373' }}>
                    <FaInfoCircle style={{ marginLeft: '0.5rem' }} />
                    الحد الأقصى للملف: 100 ميجابايت | الصيغ المدعومة: JPG, PNG, GIF, MP4
                  </small>

                  {featuredMedia ? (
                    <div className="featured-media">
                      <div className="featured-preview">
                        {featuredMedia.resource_type === 'image' ? (
                          <div className="featured-thumb">
                            <img src={featuredMedia.url} alt="الوسائط الرئيسية" />
                          </div>
                        ) : (
                          <div className="featured-placeholder">
                            {featuredMedia.resource_type === 'video' ? 
                              <FaPlayCircle /> : 
                              <FaHeadphones />
                            }
                          </div>
                        )}
                        <div className="featured-info">
                          <div className="featured-title">
                            {featuredMedia.original_filename || 'الوسائط الرئيسية'}
                          </div>
                          <div className="featured-meta">
                            {featuredMedia.format?.toUpperCase()} • {formatBytes(featuredMedia.size)}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="remove-btn"
                        onClick={() => removeMedia(0, true, false)}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ) : (
                    <div className="empty-state mt-3">
                      <FaImage />
                      <p>لم يتم اختيار وسائط رئيسية</p>
                    </div>
                  )}
                </div>

                {/* Additional Media Section */}
                <div className="mb-5">
                  <h4 className="section-header">
                    <FaImages />
                    وسائط إضافية
                  </h4>
                  
                  <button 
                    type="button"
                    onClick={() => openCloudinaryWidget(false, false)}
                    className="upload-button"
                  >
                    <FaCloudUploadAlt />
                    رفع وسائط
                  </button>

                  {additionalMedia.length > 0 ? (
                    <div className="media-preview-container mt-3">
                      <div className="media-grid">
                        {additionalMedia.map((media, index) => (
                          <div key={index} className="media-item">
                            <div className="media-preview">
                              <div className={`media-icon ${media.resource_type}`}>
                                {getFileIcon(media.format, media.resource_type)}
                              </div>
                              <div className="media-info">
                                <div className="media-name">
                                  {media.original_filename || `وسائط ${index + 1}`}
                                </div>
                                <div className="media-meta">
                                  {media.format?.toUpperCase()} • {formatBytes(media.size)}
                                </div>
                              </div>
                            </div>
                            <button
                              type="button"
                              className="remove-btn"
                              onClick={() => removeMedia(index, false, false)}
                            >
                              <FaTimes />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="empty-state mt-3">
                      <FaImages />
                      <p>لم يتم رفع وسائط إضافية</p>
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <button 
                  type="submit" 
                  disabled={loading}
                  className="submit-button"
                >
                  {loading ? (
                    <>
                      <Spinner as="span" animation="border" size="sm" />
                      جاري النشر...
                    </>
                  ) : (
                    <>
                      <FaUpload />
                      نشر المحتوى
                    </>
                  )}
                </button>
              </Form>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default UploadPage;